import React from 'react';
import ThemeToggle from '../components/ThemeToggle';

// ── Reusable helpers ──────────────────────────────────────────────────────────

type ColRow = { col: string; type: string; notes: string };

function ColTable({ rows }: { rows: ColRow[] }) {
  return (
    <div className="overflow-hidden dark:border-white/10 border border-gray-200 rounded-lg mt-2 mb-5">
      <table className="w-full">
        <thead>
          <tr className="dark:bg-white/5 bg-gray-50 dark:border-b dark:border-white/10 border-b border-gray-200">
            <th className="px-3 py-2 text-left text-xs font-semibold dark:text-white/50 text-gray-500 uppercase tracking-wide w-52">Column</th>
            <th className="px-3 py-2 text-left text-xs font-semibold dark:text-white/50 text-gray-500 uppercase tracking-wide w-20">Type</th>
            <th className="px-3 py-2 text-left text-xs font-semibold dark:text-white/50 text-gray-500 uppercase tracking-wide">Notes</th>
          </tr>
        </thead>
        <tbody className="dark:divide-y dark:divide-white/5 divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 1 ? 'dark:bg-white/3 bg-gray-50/40' : ''}>
              <td className="px-3 py-2">
                <code className="font-mono text-xs dark:text-gsw-gold text-gsw-navy font-medium">{r.col}</code>
              </td>
              <td className="px-3 py-2 font-mono text-xs text-violet-400">{r.type}</td>
              <td className="px-3 py-2 text-xs dark:text-white/60 text-gray-600">{r.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ObjHeading({ name, kind, note }: { name: string; kind: string; note?: string }) {
  return (
    <div className="mt-6 mb-1 flex items-baseline gap-2 flex-wrap">
      <code className="font-mono text-sm font-bold text-gsw-navy">{name}</code>
      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{kind}</span>
      {note && <span className="text-xs text-gray-400">{note}</span>}
    </div>
  );
}

function CodeBlock({ title, desc, code }: { title: string; desc: string; code: string }) {
  return (
    <div className="mb-6">
      <p className="text-sm font-semibold text-gray-800 mb-0.5">{title}</p>
      <p className="text-xs text-gray-500 mb-2">{desc}</p>
      <pre className="bg-[#1a1d2e] text-gray-200 rounded-lg p-4 text-xs font-mono overflow-x-auto leading-[1.75] border border-gray-700 whitespace-pre">
        {code}
      </pre>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="scroll-mt-4 mb-6">
      <div className="dark:bg-gsw-navy/40 bg-white rounded-2xl dark:border-white/10 border border-gray-200 overflow-hidden">
        <div className="h-[3px]" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)' }} />
        <div className="px-6 py-5">
          <h2 className="text-base font-semibold dark:text-gsw-gold text-gsw-navy font-playfair mb-4">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── TOC items ─────────────────────────────────────────────────────────────────

const TOC = [
  { id: 'sandbox-rules', label: 'Sandbox Rules' },
  { id: 'equity-data',   label: 'Equity Data' },
  { id: 'raw-file-data', label: 'Raw File Data' },
  { id: 'mf-data',       label: 'Mutual Fund Data' },
  { id: 'sector-values', label: 'Sector Values' },
  { id: 'code-examples', label: 'Code Examples' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

interface Props {
  fullName: string;
  backLabel: string;
  onBack: () => void;
  onLogout: () => void;
}

export default function DataReferencePage({ fullName, backLabel, onBack, onLogout }: Props) {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden dark:bg-gray-900 bg-gray-50 transition-colors duration-200">
      {/* Navbar */}
      <nav
        className="shrink-0 relative dark:bg-gsw-navy bg-white border-b dark:border-transparent border-gray-200 px-6 flex justify-between items-center transition-colors duration-200"
        style={{ paddingTop: '15px', paddingBottom: '12px' }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: 'linear-gradient(90deg, #C9A84C, #E8C97A, #C9A84C)' }}
        />
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="mr-1 flex items-center gap-1.5 text-sm dark:text-white/60 text-gray-500 dark:hover:text-white hover:text-gsw-navy transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {backLabel}
          </button>
          <img src="/gsw_logo.png" alt="GSW" className="h-9 object-contain" />
          <span className="text-base font-semibold text-gsw-gold font-playfair">
            GSW Quant Index Platform
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm dark:text-white/70 text-gray-600">
            {fullName}
            <span className="ml-2 bg-gsw-gold/20 text-gsw-gold-light text-xs font-medium px-2 py-0.5 rounded-full border border-gsw-gold/30">
              Quant
            </span>
          </span>
          <ThemeToggle />
          <button onClick={onLogout} className="text-sm dark:text-gsw-gold-light text-gsw-gold dark:hover:text-white hover:text-gsw-navy transition">
            Logout
          </button>
        </div>
      </nav>

      {/* Body */}
      <div className="flex-1 overflow-hidden flex dark:bg-[#0b1929] bg-gray-50">
        {/* TOC */}
        <aside className="w-52 shrink-0 overflow-y-auto border-r dark:border-white/10 border-gray-200 dark:bg-gsw-navy/60 bg-white py-6 px-4 transition-colors duration-200">
          <p className="text-xs font-semibold dark:text-gray-400 text-gray-400 uppercase tracking-wider mb-3 px-3">
            On this page
          </p>
          <nav className="space-y-0.5">
            {TOC.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className="w-full text-left px-3 py-2 text-sm dark:text-gray-300 text-gray-600 dark:hover:text-white hover:text-gsw-navy dark:hover:bg-white/5 hover:bg-gray-50 rounded-lg transition"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="max-w-3xl">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-gsw-navy font-playfair">Data Reference</h1>
              <p className="text-sm text-gray-500 mt-1">
                All objects and data available inside the strategy sandbox.
              </p>
            </div>

            {/* ── Sandbox Rules ───────────────────────────────────────────────── */}
            <Section id="sandbox-rules" title="Sandbox Rules">
              <div className="overflow-hidden border border-gray-200 rounded-lg mb-4">
                <table className="w-full">
                  <tbody className="divide-y divide-gray-100">
                    {[
                      {
                        label: 'Pre-injected',
                        content: (
                          <>
                            <code className="font-mono text-gsw-navy">pd</code> (pandas),{' '}
                            <code className="font-mono text-gsw-navy">np</code> (numpy),{' '}
                            <code className="font-mono text-gsw-navy">eod</code> (EodContext),{' '}
                            <code className="font-mono text-gsw-navy">mf</code> (MfContext)
                          </>
                        ),
                      },
                      {
                        label: 'Imports',
                        content: (
                          <>
                            Only <code className="font-mono">pandas</code> and{' '}
                            <code className="font-mono">numpy</code> are permitted. All other imports
                            (os, sys, subprocess, etc.) are blocked.
                          </>
                        ),
                      },
                      {
                        label: 'Output',
                        content: (
                          <>
                            Assign <code className="font-mono text-gsw-navy">index_output</code> — a
                            list of dicts{' '}
                            <code className="font-mono">
                              {'{"security_code": str, "weight": float}'}
                            </code>
                            . Weights are percentages summing to <code className="font-mono">100</code>{' '}
                            (tolerance ±0.02).
                          </>
                        ),
                      },
                      {
                        label: 'Timeout',
                        content: <>60 seconds. Strategies that exceed this are terminated.</>,
                      },
                      {
                        label: 'Dates',
                        content: (
                          <>
                            Use <code className="font-mono text-gsw-navy">int(d.strftime("%Y%m%d"))</code>{' '}
                            to convert a pandas Timestamp to an integer date key (e.g.{' '}
                            <code className="font-mono">20240510</code>).{' '}
                            <code className="font-mono">strftime</code> works on all pandas Timestamps.
                          </>
                        ),
                      },
                    ].map((row, i) => (
                      <tr key={row.label} className={i % 2 === 1 ? 'bg-gray-50/40' : ''}>
                        <td className="px-3 py-2.5 w-28 text-xs font-semibold text-gray-500 align-top pt-3">
                          {row.label}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-700">{row.content}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mb-1">Output format:</p>
              <pre className="bg-[#1a1d2e] text-gray-200 rounded-lg p-4 text-xs font-mono overflow-x-auto leading-[1.75] border border-gray-700 whitespace-pre">{`index_output = [
    {"security_code": "RELIANCE",  "weight": 5.12},
    {"security_code": "119598",    "weight": 3.88},  # AMFI code for MF
    {"security_code": "119598_D",  "weight": 2.00},  # scheme code
]
# Weights must sum to 100 (± 0.02)`}</pre>
            </Section>

            {/* ── Equity Data ─────────────────────────────────────────────────── */}
            <Section id="equity-data" title="Equity Data">
              <ObjHeading name="eod.prices" kind="DataFrame" note="all loaded trading dates" />
              <ColTable rows={[
                { col: 'date',       type: 'datetime', notes: 'Trading date' },
                { col: 'symbol',     type: 'str',      notes: 'NSE ticker (e.g. "RELIANCE")' },
                { col: 'open',       type: 'float',    notes: 'Opening price' },
                { col: 'high',       type: 'float',    notes: 'Day high' },
                { col: 'low',        type: 'float',    notes: 'Day low' },
                { col: 'close',      type: 'float',    notes: 'Closing price' },
                { col: 'prev_close', type: 'float',    notes: 'Previous session close' },
                { col: 'volume',     type: 'int',      notes: 'Shares traded' },
              ]} />

              <ObjHeading name="eod.fundamentals" kind="DataFrame" note="one row per active symbol" />
              <ColTable rows={[
                { col: 'symbol',               type: 'str',   notes: 'NSE ticker' },
                { col: 'isin',                 type: 'str',   notes: 'ISIN code' },
                { col: 'sector',               type: 'str',   notes: 'Sector name — see Sector Values' },
                { col: 'market_cap_category',  type: 'str',   notes: '"Large Cap" / "Mid Cap" / "Small Cap"' },
                { col: 'market_cap_cr',        type: 'float', notes: 'Market cap in Crores' },
                { col: 'pe_ratio',             type: 'float', notes: 'Price-to-earnings ratio' },
                { col: 'pb_ratio',             type: 'float', notes: 'Price-to-book ratio' },
                { col: 'dividend_yield',       type: 'float', notes: 'Dividend yield (%)' },
              ]} />

              <ObjHeading name="eod.sectors" kind="DataFrame" note="symbol-level sector mapping" />
              <ColTable rows={[
                { col: 'symbol',     type: 'str', notes: 'NSE ticker' },
                { col: 'sector',     type: 'str', notes: '' },
                { col: 'industry',   type: 'str', notes: '' },
                { col: 'mes_sector', type: 'str', notes: 'Bilav sector classification' },
              ]} />

              <div className="overflow-hidden border border-gray-200 rounded-lg mt-6 mb-5">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-40">Attribute</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { attr: 'eod.universe',  type: 'list[str]', desc: 'Sorted list of all active NSE symbols' },
                      { attr: 'eod.returns',   type: 'Series',    desc: 'symbol → single-day % return (open → close)' },
                      { attr: 'eod.high_52w',  type: 'Series',    desc: 'symbol → 52-week high price' },
                      { attr: 'eod.low_52w',   type: 'Series',    desc: 'symbol → 52-week low price' },
                      { attr: 'eod.prev_close',type: 'Series',    desc: 'symbol → previous close from Bilav file' },
                      { attr: 'eod.as_of',     type: 'str',       desc: 'ISO date of the data snapshot, e.g. "2024-05-10"' },
                    ].map((r, i) => (
                      <tr key={r.attr} className={i % 2 === 1 ? 'bg-gray-50/40' : ''}>
                        <td className="px-3 py-2"><code className="font-mono text-xs text-gsw-navy font-medium">{r.attr}</code></td>
                        <td className="px-3 py-2 font-mono text-xs text-violet-600">{r.type}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{r.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ObjHeading name="eod.invits" kind="DataFrame" note="InvITs and REITs — same schema as eod.prices" />
              <ColTable rows={[
                { col: 'symbol',                    type: 'str',      notes: 'NSE ticker' },
                { col: 'date',                      type: 'datetime', notes: 'Trading date' },
                { col: 'open / high / low / close', type: 'float',    notes: 'OHLC prices' },
                { col: 'volume',                    type: 'int',      notes: 'Units traded' },
              ]} />

              <ObjHeading name="eod.bonds" kind="DataFrame" note="listed bond data" />
              <ColTable rows={[
                { col: 'isin',          type: 'str',      notes: '' },
                { col: 'name',          type: 'str',      notes: 'Bond name' },
                { col: 'date',          type: 'datetime', notes: '' },
                { col: 'open / high / low / close', type: 'float', notes: 'OHLC prices' },
                { col: 'ytm',           type: 'float',    notes: 'Yield to maturity' },
                { col: 'rating',        type: 'str',      notes: 'Credit rating' },
                { col: 'maturity_date', type: 'datetime', notes: 'Bond maturity date' },
              ]} />
            </Section>

            {/* ── Raw File Data ────────────────────────────────────────────────── */}
            <Section id="raw-file-data" title="Raw File Data">
              <p className="text-xs text-gray-500 mb-2">
                Source files from the most recent complete data folder. Prefer the structured
                attributes above for standard strategies; use these for ad-hoc analysis.
              </p>

              <ObjHeading name="eod.we" kind="DataFrame" note="Vidal WE file — equity OHLCV" />
              <ColTable rows={[
                { col: 'record_type',             type: 'str',   notes: '' },
                { col: 'exchange',                type: 'str',   notes: '' },
                { col: 'date',                    type: 'str',   notes: 'Trade date' },
                { col: 'code',                    type: 'str',   notes: 'NSE symbol' },
                { col: 'type',                    type: 'str',   notes: 'Series (EQ, BE, etc.)' },
                { col: 'name',                    type: 'str',   notes: 'Company name' },
                { col: 'open / high / low / close', type: 'float', notes: 'OHLC prices' },
                { col: 'volume',                  type: 'int',   notes: 'Shares traded' },
                { col: 'trades',                  type: 'int',   notes: 'Number of trades' },
                { col: 'value',                   type: 'float', notes: 'Total traded value' },
              ]} />

              <ObjHeading name="eod.wy" kind="DataFrame" note="Vidal WY file — InvIT/REIT prices (same columns as eod.we)" />

              <ObjHeading name="eod.xm" kind="DataFrame" note="Vidal XM file — security master" />
              <ColTable rows={[
                { col: 'record_type',  type: 'str',   notes: '' },
                { col: 'internal_id',  type: 'str',   notes: '' },
                { col: 'company_name', type: 'str',   notes: 'Full company name' },
                { col: 'isin',         type: 'str',   notes: '' },
                { col: 'nse_symbol',   type: 'str',   notes: 'NSE ticker' },
                { col: 'series',       type: 'str',   notes: '' },
                { col: 'face_value',   type: 'float', notes: '' },
                { col: 'bse_code',     type: 'str',   notes: '' },
                { col: 'industry_name',type: 'str',   notes: '' },
                { col: 'sector_name',  type: 'str',   notes: '' },
              ]} />

              <ObjHeading name="eod.xc" kind="DataFrame" note="Vidal XC file — corporate actions" />
              <ColTable rows={[
                { col: 'record_type', type: 'str', notes: '' },
                { col: 'isin',        type: 'str', notes: '' },
                { col: 'bse_code',    type: 'str', notes: '' },
                { col: 'nse_symbol',  type: 'str', notes: 'NSE ticker' },
                { col: 'series',      type: 'str', notes: '' },
                { col: 'ex_date',     type: 'str', notes: 'Ex-action date' },
                { col: 'action_type', type: 'str', notes: 'Dividend / Split / Bonus / etc.' },
                { col: 'record_date', type: 'str', notes: '' },
                { col: 'payment_date',type: 'str', notes: '' },
                { col: 'details',     type: 'str', notes: 'Action details' },
              ]} />

              <ObjHeading name="eod.company" kind="DataFrame" note="Vidal CompanyMaster Excel" />
              <ColTable rows={[
                { col: 'Company',               type: 'str',   notes: 'Full company name' },
                { col: 'ISIN Code',             type: 'str',   notes: '' },
                { col: 'NSE Code',              type: 'str',   notes: 'NSE ticker' },
                { col: 'BSE Demat Code',        type: 'str',   notes: '' },
                { col: 'Sector Classification', type: 'str',   notes: '' },
                { col: 'MarketCap',             type: 'str',   notes: 'Large / Mid / Small' },
                { col: 'Market cap in Cr.',     type: 'float', notes: 'Market cap in Crores' },
                { col: 'PERatio',               type: 'float', notes: '' },
                { col: 'PBRatio',               type: 'float', notes: '' },
                { col: 'DividendYield',         type: 'float', notes: '' },
              ]} />

              <ObjHeading name="eod.nav" kind="DataFrame" note="VR NAVUpdate CSV — raw file (use mf.nav for historical DB)" />
              <ColTable rows={[
                { col: 'SchemeCode', type: 'str',   notes: 'VR scheme code' },
                { col: 'Date',       type: 'str',   notes: 'NAV date' },
                { col: 'NAV',        type: 'float', notes: 'Net asset value' },
                { col: 'AmfiCode',   type: 'int',   notes: 'AMFI numeric code' },
              ]} />
            </Section>

            {/* ── Mutual Fund Data ─────────────────────────────────────────────── */}
            <Section id="mf-data" title="Mutual Fund Data">
              <p className="text-xs text-gray-500 mb-2">
                VR file data under <code className="font-mono">eod.*</code>; historical NAV database
                under <code className="font-mono">mf.*</code>. Primary join key across all MF tables
                is <code className="font-mono">SchemeCode</code>.
              </p>

              <ObjHeading name="eod.mf_factsheet" kind="DataFrame" note="VR Factsheet CSV — one row per fund" />
              <ColTable rows={[
                { col: 'SchemeCode',            type: 'str',   notes: 'VR scheme code — primary join key' },
                { col: 'Classification',        type: 'str',   notes: '"Equity: Large Cap", "Debt: Short Duration", …' },
                { col: 'Standard Deviation',    type: 'float', notes: 'Annualised standard deviation' },
                { col: 'Sharpe Ratio',          type: 'float', notes: 'Risk-adjusted return' },
                { col: 'Beta',                  type: 'float', notes: 'Market sensitivity' },
                { col: 'Alpha',                 type: 'float', notes: 'Excess return vs benchmark' },
                { col: 'Sortino',               type: 'float', notes: 'Downside risk ratio' },
                { col: 'Net Assets (Rs Cr)',    type: 'float', notes: 'AUM in Crores' },
                { col: 'ExpenseRatio',          type: 'float', notes: 'Annual expense ratio (%)' },
                { col: 'Risk Grade',            type: 'str',   notes: '' },
                { col: 'Return Grade',          type: 'str',   notes: '' },
                { col: 'SCHEME RETURNS-1 YEAR', type: 'float', notes: '1-year return (%)' },
                { col: 'SCHEME RETURNS-3 YEAR', type: 'float', notes: '3-year return (%)' },
              ]} />

              <ObjHeading name="eod.mf_main" kind="DataFrame" note="VR MainUpdate CSV — fund registry" />
              <ColTable rows={[
                { col: 'MutualFund',     type: 'str', notes: 'AMC name' },
                { col: 'SchemeName',     type: 'str', notes: 'Fund name' },
                { col: 'SchemeCode',     type: 'str', notes: 'VR scheme code — join key' },
                { col: 'AMFICode',       type: 'str', notes: 'AMFI numeric code (as string)' },
                { col: 'Nature',         type: 'str', notes: 'Open-ended / Close-ended' },
                { col: 'Classification', type: 'str', notes: 'Fund category' },
                { col: 'PlanType',       type: 'str', notes: 'Direct / Regular' },
                { col: 'Status',         type: 'str', notes: '"Live" / "Wound Up"' },
              ]} />

              <ObjHeading name="eod.mf_equity" kind="DataFrame" note="VR Equity XLS — equity fund portfolio stats" />
              <ColTable rows={[
                { col: 'Scheme Code',         type: 'str',   notes: 'VR scheme code (note: space in name)' },
                { col: 'Scheme',              type: 'str',   notes: 'Fund name' },
                { col: 'LargeCapPercentage',  type: 'float', notes: '% allocation to large caps' },
                { col: 'MidCapPercentage',    type: 'float', notes: '% allocation to mid caps' },
                { col: 'SmallCapPercentage',  type: 'float', notes: '% allocation to small caps' },
                { col: 'PEScore',             type: 'float', notes: 'Portfolio PE score' },
                { col: 'PBScore',             type: 'float', notes: 'Portfolio PB score' },
                { col: 'AUM (In Crores)',     type: 'float', notes: 'AUM in Crores' },
              ]} />

              <ObjHeading name="eod.mf_debt" kind="DataFrame" note="VR Debt XLS — historical (multiple rows per scheme; deduplicate to latest Date before joining)" />
              <ColTable rows={[
                { col: 'Scheme Code',          type: 'str',      notes: 'VR scheme code (note: space in name)' },
                { col: 'Scheme',               type: 'str',      notes: 'Fund name' },
                { col: 'Date',                 type: 'datetime', notes: 'Record date — file has multiple rows per scheme' },
                { col: 'AvgYTM',               type: 'float',    notes: 'Average yield to maturity (%)' },
                { col: 'Duration',             type: 'float',    notes: 'Modified duration (years)' },
                { col: 'AverageCreditRating',  type: 'float',    notes: 'Weighted credit rating (higher = safer)' },
                { col: 'AUM (In Crores)',       type: 'float',    notes: 'AUM in Crores' },
              ]} />

              <ObjHeading name="eod.mf_holdings" kind="DataFrame" note="VR HoldingsUpdate TXT — holdings per fund" />
              <ColTable rows={[
                { col: 'AMFICode',       type: 'str',   notes: 'AMFI code' },
                { col: 'SchemeCode',     type: 'str',   notes: 'VR scheme code' },
                { col: 'ISIN',           type: 'str',   notes: 'Stock ISIN' },
                { col: 'PortfolioDate',  type: 'str',   notes: 'Holdings date' },
                { col: 'SectorName',     type: 'str',   notes: 'Sector of holding' },
                { col: 'CompanyName',    type: 'str',   notes: '' },
                { col: 'CompanyFullName',type: 'str',   notes: '' },
                { col: 'AssetClass',     type: 'str',   notes: 'Equity / Debt / Cash / etc.' },
                { col: 'PctToAUM',       type: 'float', notes: '% of fund AUM' },
                { col: 'ValueINR',       type: 'float', notes: 'Value in INR' },
                { col: 'MarketCap',      type: 'str',   notes: 'Large / Mid / Small' },
                { col: 'InvestmentStyle',type: 'str',   notes: 'Value / Growth / Blend' },
                { col: 'CreditRating',   type: 'str',   notes: 'For debt holdings' },
              ]} />

              <ObjHeading name="eod.mf_dividend" kind="DataFrame" note="VR DividendUpdate CSV" />
              <ColTable rows={[
                { col: 'SchemeCode',              type: 'str',   notes: 'VR scheme code' },
                { col: 'RecordDate',              type: 'str',   notes: '' },
                { col: 'ExDividendDate',          type: 'str',   notes: '' },
                { col: 'Individual Dividend (%)', type: 'float', notes: 'Dividend per unit (%)' },
                { col: 'AMFICode',                type: 'str',   notes: 'AMFI code' },
              ]} />

              <div className="mt-6 pt-5 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  MF Database (mf.*)
                </p>

                <ObjHeading name="mf.nav" kind="DataFrame" note="DB — all historical NAV records" />
                <ColTable rows={[
                  { col: 'scheme_code', type: 'str',   notes: 'VR scheme code' },
                  { col: 'amfi_code',   type: 'str',   notes: 'AMFI code (stored as string)' },
                  { col: 'nav',         type: 'float', notes: 'Net asset value' },
                  { col: 'date',        type: 'date',  notes: 'NAV date' },
                ]} />

                <ObjHeading name="mf.universe" kind="list[str]" note="all valid MF security codes — AMFI codes as strings" />
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
                  <strong>MF output codes:</strong> use the AMFI numeric code as a string (e.g.{' '}
                  <code className="font-mono">"119598"</code>), obtained by joining{' '}
                  <code className="font-mono">eod.mf_factsheet["SchemeCode"]</code> →{' '}
                  <code className="font-mono">eod.nav["SchemeCode"]</code> →{' '}
                  <code className="font-mono">eod.nav["AmfiCode"]</code>.
                </div>
              </div>
            </Section>

            {/* ── Sector Values ────────────────────────────────────────────────── */}
            <Section id="sector-values" title="Sector Values">
              <p className="text-xs text-gray-500 mb-4">
                Valid strings for{' '}
                <code className="font-mono">eod.fundamentals["sector"]</code>. Use exact case when
                filtering.
              </p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                {[
                  'Automobile', 'Capital Goods', 'Chemicals', 'Communication',
                  'Construction', 'Consumer Discretionary', 'Consumer Staples', 'Diversified',
                  'Energy', 'Fast Moving Consumer Goods', 'Financial', 'Healthcare',
                  'Insurance', 'Materials', 'Media Entertainment & Publication', 'Metals & Mining',
                  'Others', 'Services', 'Technology', 'Textiles',
                ].map((s) => (
                  <div key={s} className="flex items-center gap-2 py-1.5 border-b border-gray-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-gsw-gold shrink-0" />
                    <code className="text-xs font-mono text-gray-700">{s}</code>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── Code Examples ────────────────────────────────────────────────── */}
            <Section id="code-examples" title="Code Examples">
              <CodeBlock
                title="Filtering the universe"
                desc="Large-cap stocks in a given sector that traded every day of the past 20 sessions."
                code={`fund = eod.fundamentals.set_index("symbol")

screen = fund[
    (fund["market_cap_category"] == "Large Cap") &
    (fund["sector"] == "Technology") &
    (fund["pe_ratio"].between(5, 50)) &
    (fund["dividend_yield"] >= 1.0)
].index.tolist()

# Keep only liquid symbols (traded all 20 recent days)
px       = eod.prices.copy()
recent   = sorted(px["date"].unique())[-20:]
days_traded = px[px["date"].isin(recent)].groupby("symbol")["date"].nunique()
liquid   = days_traded[days_traded >= 20].index
screen   = [s for s in screen if s in liquid]`}
              />
              <CodeBlock
                title="Computing price momentum"
                desc="12-1 month momentum: return from 252 sessions ago to 21 sessions ago (skips short-term reversal)."
                code={`px = eod.prices.copy()
px["close"] = pd.to_numeric(px["close"], errors="coerce")
px["date"]  = pd.to_datetime(px["date"],  errors="coerce")
px = px.dropna(subset=["symbol", "date", "close"]).sort_values(["symbol", "date"])

px_pivot = px.pivot_table(index="date", columns="symbol", values="close")
dates    = sorted(px_pivot.index)

if len(dates) >= 252:
    price_21  = px_pivot.loc[dates[-21]].dropna()
    price_252 = px_pivot.loc[dates[-252]].dropna()
    common    = price_21.index.intersection(price_252.index)
    momentum  = (price_21[common] / price_252[common] - 1) * 100  # %`}
              />
              <CodeBlock
                title="Building index_output with a per-stock cap"
                desc="Score-proportional weights capped at 5% per stock, summing to exactly 100."
                code={`# scores: Series indexed by symbol, higher = better
top30 = scores.sort_values(ascending=False).head(30).to_frame("score")
top30["weight"] = top30["score"] / top30["score"].sum()

for _ in range(20):
    capped = top30["weight"] > 0.05
    if not capped.any():
        break
    excess = (top30.loc[capped, "weight"] - 0.05).sum()
    top30.loc[capped, "weight"] = 0.05
    uncapped = ~capped
    if top30.loc[uncapped, "weight"].sum() > 0:
        top30.loc[uncapped, "weight"] += (
            excess * top30.loc[uncapped, "weight"]
            / top30.loc[uncapped, "weight"].sum()
        )

top30["weight_pct"] = (top30["weight"] / top30["weight"].sum() * 100).round(4)
diff = 100.0 - top30["weight_pct"].sum()
top30.loc[top30["weight_pct"].idxmax(), "weight_pct"] += round(diff, 4)

index_output = [
    {"security_code": str(sym), "weight": float(row["weight_pct"])}
    for sym, row in top30.iterrows()
    if row["weight_pct"] > 0
]`}
              />
              <CodeBlock
                title="Date arithmetic with strftime"
                desc="Converting a pandas Timestamp to an integer date key — required when comparing against Vidal raw-file date columns (XC, XM) which store dates as integers like 20240510."
                code={`# strftime("%Y%m%d") is the standard way to produce an integer date key
d          = pd.Timestamp(eod.as_of)         # or: recent_days[-1]
cutoff_int = int(d.strftime("%Y%m%d"))       # e.g. 20240510

# Example: exclude stocks with a corporate action on or after the cutoff
xc = eod.xc.copy()
xc["ex_date"]     = pd.to_numeric(xc["ex_date"],     errors="coerce")
xc["action_type"] = xc["action_type"].astype(str).str.strip().str.upper()
xc["nse_symbol"]  = xc["nse_symbol"].astype(str).str.strip().str.upper()

EXCLUDE = {"R", "S", "M", "A"}   # rights, splits, mergers, amalgamations
dirty = xc[
    xc["action_type"].isin(EXCLUDE) &
    (xc["ex_date"] >= cutoff_int)
]["nse_symbol"].unique()

screen = [s for s in screen if s not in dirty]

# Other common strftime formats:
# d.strftime("%Y-%m-%d")   -> "2024-05-10"  (ISO string)
# d.strftime("%d%m%Y")     -> "10052024"     (Vidal filename date portion)`}
              />
            </Section>

            <div className="h-10" />
          </div>
        </div>
      </div>
    </div>
  );
}
